import { useEffect, useState } from 'react';
import { api, type NetLeaseBenchmark } from '../../lib/api';

/**
 * NB1 (plan Task 7; brief section 6) - the Benchmark panel beside the Decision Scorecard, on a single-tenant deal: where
 * the going-in cap sits against the national asking range for that tenant at that term, in basis points and dollars.
 *
 * ⭐ Ruling 40: every number AND every row's words come from the server's one calculator (`GET /api/net-lease/benchmark`,
 *    `benchmark.lines`); this only lays them out. It wrote three rows itself once and got two wrong - "enter a price" with no
 *    lease term, "no comps yet" over comps it had read and left out (NB1 review I2, I3).
 * ⛔ It never scores and never touches the scorecard's thresholds; a deal outside its range is a finding, not an error.
 * ⛔ Every figure says its source, its date and asking-or-closed; an error SAYS so - an empty panel reads as "no data".
 * ⛔ A fetch per change of the deal's numbers, debounced; each load's answer applies only while it is the latest.
 */
interface Props {
  tenantName: string; leaseEnd: string | null; noi: number; price: number;
  leaseType?: string | null; yearBuilt?: number | null; landSf?: number | null; loanAmount?: number | null; annualDebtService?: number | null;
}

const positive = (n: number | null | undefined): n is number => n != null && Number.isFinite(n) && n > 0;

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-x-4 border-t px-4 py-2 sm:grid-cols-[15rem_1fr]">
      <div className="text-gray-600">{label}</div>
      <div className="font-medium">{children}</div>
    </div>
  );
}

export default function BenchmarkPanel(p: Props) {
  const [b, setB] = useState<NetLeaseBenchmark | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    const timer = setTimeout(() => {
      api.getNetLeaseBenchmark({
        tenant: p.tenantName, lease_end: p.leaseEnd, noi: positive(p.noi) ? p.noi : null, price: positive(p.price) ? p.price : null,
        lease_type: p.leaseType ?? null, year_built: p.yearBuilt ?? null, land_sf: p.landSf ?? null,
        loan: p.loanAmount ?? null, debt_service: p.annualDebtService ?? null,
      })
        .then((r) => { if (live) { setB(r.benchmark); setFailed(false); } })
        .catch(() => { if (live) setFailed(true); });
    }, 400);
    return () => { live = false; clearTimeout(timer); };
  }, [p.tenantName, p.leaseEnd, p.noi, p.price, p.leaseType, p.yearBuilt, p.landSf, p.loanAmount, p.annualDebtService]);

  const frame = (body: React.ReactNode) => (
    <section aria-label="Market benchmark" className="overflow-hidden rounded-xl border text-sm">
      <div className="bg-gray-900 px-4 py-2 font-semibold text-white">{`Market benchmark - ${b?.match?.tenant ?? p.tenantName}`}</div>
      {body}
    </section>
  );
  if (failed) return frame(<p className="px-4 py-3 text-gray-600">Could not load the market benchmark.</p>);
  if (!b) return frame(<p className="px-4 py-3 text-gray-500">Loading the market benchmark…</p>);
  if (!b.match || !b.lines) return frame(<p className="px-4 py-3 text-gray-600">{b.paragraph}</p>);

  const used = b.term?.used ?? null;
  const said = b.lines;
  return frame(
    <div>
      {said.alias && <p className="px-4 pt-2 text-gray-600">{said.alias}</p>}
      <Row label={used ? `National range, ${used}-year term` : 'National range'}>{said.national}</Row>
      {/* The DEAL's own term - not the national row's, which can be a substitute (the 123rd). */}
      <Row label="California median, this deal's term">{said.california}</Row>
      <Row label="This deal">{said.deal}</Row>
      {said.price !== null && <Row label="Price against the national midpoint">{said.price}</Row>}
      {said.treasury !== null && <Row label="Spread to the 10-year Treasury">{said.treasury}</Row>}
      {said.ten_year !== null && <Row label="What 10 firm years would price at">{said.ten_year}</Row>}
      <Row label="Rent-to-sales">{said.rent_to_sales}</Row>
      <Row label="Price per SF of land">{said.land}</Row>
      {b.flags.length > 0 && (
        <ul className="list-disc space-y-1 border-t bg-warning-wash px-8 py-2">{b.flags.map((f) => <li key={f}>{f}</li>)}</ul>
      )}
      {/* The notes no row already says - the server decides which (ruling 40; NB1 re-review minor 2). */}
      {said.notes.length > 0 && <ul className="space-y-1 border-t px-4 py-2 text-gray-600">{said.notes.map((n) => <li key={n}>{n}</li>)}</ul>}
      <p className="border-t px-4 py-2">{b.paragraph}</p>
      <p className="border-t px-4 py-2 text-xs text-gray-500">
        National asking figures for typical properties - a deal outside its range is a finding to explain, not an error. The tenant ranges are
        The Boulder Group&apos;s published research: an internal reference, not for client products without their permission.
      </p>
    </div>,
  );
}
